import { Check, Moon, Palette, RotateCcw, Sun } from "lucide-react";
import { useAppearance } from "../../../theme/AppearanceProvider";
import { BACKGROUND_PRESETS, PRIMARY_PRESETS } from "../../../theme/appearanceTokens";
import { Button } from "../../ui/button";
import { useEnglishText } from "../../../text/useEnglishText.js";
import "../../../styles/Appearance.css";

export default function AppearanceTab() {
  const { t } = useEnglishText();
  const { preferences, mode, updatePreference, updatePreferences, resetPreferences } = useAppearance();

  const themeChoices = [
    { value: "light", icon: Sun, label: t("settings.appearance.light"), description: t("settings.appearance.lightDescription") },
    { value: "dark", icon: Moon, label: t("settings.appearance.dark"), description: t("settings.appearance.darkDescription") },
    { value: "custom", icon: Palette, label: t("settings.appearance.custom"), description: t("settings.appearance.customDescription") },
  ];

  const selectPrimary = (value) => updatePreferences({ mode: "custom", primaryColor: value, shellColorSource: "primary" });
  const selectBackground = (value) => updatePreferences({ mode: "custom", backgroundColor: value, shellColorSource: "background" });
  const isPresetPrimary = PRIMARY_PRESETS.some((preset) => preset.value === preferences.primaryColor);
  const isPresetBackground = BACKGROUND_PRESETS.some((preset) => preset.value === preferences.backgroundColor);

  return (
    <section className="appearance-settings" aria-labelledby="appearance-title">
      <div className="appearance-settings__heading">
        <div>
          <h2 id="appearance-title">{t("settings.appearance.title")}</h2>
          <p>{t("settings.appearance.description")}</p>
        </div>
        <Button className="appearance-settings__reset" variant="outline" onClick={resetPreferences}>
          <RotateCcw aria-hidden="true" />
          <span>{t("settings.appearance.reset")}</span>
        </Button>
      </div>

      <fieldset className="appearance-choice-group">
        <legend>{t("settings.appearance.theme")}</legend>
        <div className="appearance-theme-options">
          {themeChoices.map(({ value, icon: Icon, label, description }) => (
            <button
              key={value}
              type="button"
              className={`appearance-theme-option ${mode === value ? "is-selected" : ""}`}
              aria-pressed={mode === value}
              onClick={() => updatePreference("mode", value)}
            >
              <Icon aria-hidden="true" />
              <span><strong>{label}</strong><small>{description}</small></span>
              {mode === value && <Check className="appearance-theme-option__check" aria-hidden="true" />}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="appearance-choice-group">
        <legend>{t("settings.appearance.primaryColor")}</legend>
        <p className="appearance-choice-group__hint">{t("settings.appearance.primaryColorHint")}</p>
        <div className="appearance-swatches">
          {PRIMARY_PRESETS.map((preset) => (
            <button
              key={preset.value}
              type="button"
              className={`appearance-swatch ${mode === "custom" && preferences.primaryColor === preset.value ? "is-selected" : ""}`}
              style={{ "--swatch-color": preset.value }}
              aria-pressed={mode === "custom" && preferences.primaryColor === preset.value}
              onClick={() => selectPrimary(preset.value)}
            >
              <span className="appearance-swatch__sample" aria-hidden="true" />
              <span>{t(preset.labelKey, preset.name)}</span>
            </button>
          ))}
          <label className={`appearance-swatch appearance-color-picker ${mode === "custom" && !isPresetPrimary ? "is-selected" : ""}`}>
            <input
              type="color"
              value={preferences.primaryColor}
              aria-label={t("settings.appearance.customColor")}
              onChange={(event) => selectPrimary(event.target.value)}
            />
            <span>{t("settings.appearance.customColor")}</span>
            <small>{preferences.primaryColor.toUpperCase()}</small>
          </label>
        </div>
      </fieldset>

      <fieldset className="appearance-choice-group">
        <legend>{t("settings.appearance.backgroundColor")}</legend>
        <p className="appearance-choice-group__hint">{t("settings.appearance.backgroundColorHint")}</p>
        <div className="appearance-swatches">
          {BACKGROUND_PRESETS.map((preset) => (
            <button
              key={preset.value}
              type="button"
              className={`appearance-swatch ${mode === "custom" && preferences.backgroundColor === preset.value ? "is-selected" : ""}`}
              style={{ "--swatch-color": preset.value }}
              aria-pressed={mode === "custom" && preferences.backgroundColor === preset.value}
              onClick={() => selectBackground(preset.value)}
            >
              <span className="appearance-swatch__sample" aria-hidden="true" />
              <span>{t(preset.labelKey, preset.name)}</span>
            </button>
          ))}
          <label className={`appearance-swatch appearance-color-picker ${mode === "custom" && !isPresetBackground ? "is-selected" : ""}`}>
            <input
              type="color"
              value={preferences.backgroundColor}
              aria-label={t("settings.appearance.customBackground")}
              onChange={(event) => selectBackground(event.target.value)}
            />
            <span>{t("settings.appearance.customBackground")}</span>
            <small>{preferences.backgroundColor.toUpperCase()}</small>
          </label>
        </div>
      </fieldset>

      <div className="appearance-controls-grid">
        <AppearanceSegment
          title={t("settings.appearance.corners")}
          value={preferences.radius}
          onChange={(value) => updatePreference("radius", value)}
          options={[
            { value: "compact", label: t("settings.appearance.compact") },
            { value: "default", label: t("settings.appearance.default") },
            { value: "rounded", label: t("settings.appearance.rounded") },
          ]}
        />
        <AppearanceSegment
          title={t("settings.appearance.density")}
          value={preferences.density}
          onChange={(value) => updatePreference("density", value)}
          options={[
            { value: "compact", label: t("settings.appearance.compact") },
            { value: "comfortable", label: t("settings.appearance.comfortable") },
            { value: "spacious", label: t("settings.appearance.spacious") },
          ]}
        />
        <AppearanceSegment
          title={t("settings.appearance.textSize")}
          value={preferences.fontSize}
          onChange={(value) => updatePreference("fontSize", value)}
          options={[
            { value: "small", label: t("settings.appearance.small") },
            { value: "default", label: t("settings.appearance.default") },
            { value: "large", label: t("settings.appearance.large") },
          ]}
        />
      </div>

      <section className="appearance-preview" aria-label={t("settings.appearance.preview")}>
        <div>
          <strong>{t("settings.appearance.previewTitle")}</strong>
          <p>{t("settings.appearance.previewDescription")}</p>
        </div>
        <span className="appearance-preview__action">{t("settings.appearance.previewAction")}</span>
      </section>

      <p className="appearance-settings__note">{t("settings.appearance.savedLocally")}</p>
    </section>
  );
}

function AppearanceSegment({ title, value, options, onChange }) {
  return (
    <fieldset className="appearance-choice-group appearance-segment-group">
      <legend>{title}</legend>
      <div className="appearance-segment">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            className={value === option.value ? "is-selected" : ""}
            aria-pressed={value === option.value}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </fieldset>
  );
}
